import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { C } from "../../styles/tokens";
import PLANS from "../../constants/plans.json";
import Info from "./PlanInfo";
import Spinner from "../Spinner";
import { useBilling } from "../../hooks/useBilling";
import { useOrganisation } from "../../http-hooks/organisation/useOrganisation";
import { addMixPanelEvent } from "../../store/actions";

// ── Plan helpers ──────────────────────────────────────────────────────────────

/** Free-tier plan ids — checkout/upgrade doesn't support "upgrading" to these; downgrading to
 * Free happens through Cancel Subscription instead, so the Free card's CTA is disabled.
 * Exported (rather than re-declared) so callers like planCompare.js can't drift from this list. */
export const FREE_PLAN_IDS = ["preview-plan-usd", "preview-plan-inr-2"];

/**
 * Canonical tier order — drives upgrade/downgrade comparisons.
 * Adding a new plan version never changes this; only new *tiers* need a new entry.
 */
export const PLAN_TIER_ORDER = ["free", "mini", "starter", "light", "full", "enterprise"];

/** Finds the plans.json entry matching any of a plan_id's usd/inr/annual fields. */
const findPlanById = (planId) => {
  if (!planId) return null;
  return PLANS.find(
    (p) =>
      p.plan_id_usd === planId ||
      p.plan_id_inr === planId ||
      p.plan_id_usd_annual === planId ||
      p.plan_id_inr_annual === planId,
  ) ?? null;
};

/** Resolve which plan_family a given plan_id belongs to (searches all versions). */
export const getPlanFamily = (planId) => findPlanById(planId)?.plan_family ?? null;

/** Tier rank for a family string — unknown families return -1. */
export const getTierRank = (family) => PLAN_TIER_ORDER.indexOf(family ?? "");

/** Maps the 6-value internal plan_family to the 5-value Mixpanel tier scheme. */
const FAMILY_TO_TIER = {
  free: "free",
  mini: "intro",
  starter: "starter",
  light: "standard",
  full: "advanced",
  enterprise: "advanced",
};


export const getPlanTier = (planId) => (!planId ? "free" : (FAMILY_TO_TIER[getPlanFamily(planId)] ?? "unknown"));

/**
 * A plan card is "current" if the org is on ANY version of the same family.
 * This handles v1→v2 cross-version mismatches correctly.
 */
export const isCurrentPlan = (orgPlanId, plan) => {
  const orgFamily = getPlanFamily(orgPlanId);
  return orgFamily != null && orgFamily === plan.plan_family;
};

/** The "featured / most popular" plan */
export const isPopularPlan = (plan) =>
  plan.plan_id_usd === "light-plan-usd-unlimited" ||
  plan.plan_id_inr === "light-plan-inr-unlimited";

/** All active plans shown in plan cards, Free included (matches the POC's 4-card layout). */
export const getVisiblePlans = () => PLANS.filter((p) => p.is_active);

/** Free-tier plan ids — checkout/upgrade doesn't support "upgrading" to these; downgrading to
 * Free happens through Cancel Subscription instead, so the Free card's CTA is disabled. */
export const isFreePlanId = (planId) => FREE_PLAN_IDS.includes(planId);

/**
 * Display name for an org's plan_id, sourced from plans.json's plan_title — the same field the
 * plan cards and compare table use — instead of mangling the plan_id string (that legacy path,
 * still in utils/billingFormat.js's planIdToString/dashCaseToSentenceCase, produces things like
 * "Light plan  unlimited" and would drift from a marketing rename like Light -> Creator).
 * Prefers the *active* version of the plan's family so a legacy/deprecated plan_id still shows
 * today's name for that tier. Returns null if the id isn't in the catalog at all (e.g. a very old
 * or custom plan_id) so callers can fall back to the legacy derivation.
 */
export const getPlanTitle = (planId) => {
  const exact = findPlanById(planId);
  if (!exact) return null;
  const active = PLANS.find((p) => p.plan_family === exact.plan_family && p.is_active);
  return (active ?? exact).plan_title ?? null;
};

// ── CheckIcon ─────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="8" cy="8" r="7" fill={C.p50} stroke={C.p200} />
      <path d="M4.5 8.2l2.3 2.3 4.2-4.8" stroke={C.p600} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── PlanCardsGrid ─────────────────────────────────────────────────────────────

/**
 * Reusable plan cards grid.
 *
 * Props:
 *   org_id    – organisation ID (required)
 *   onSuccess – called after a successful upgrade / downgrade / resume
 *   source    – checkout source tag for analytics (default: "plan_cards")
 *   upgradePromptId – ID of the upgrade prompt this grid is shown inside, if any,
 *                      so UPGRADE_PROMPT_CLICKED can be tied back to its shown/dismissed
 */
export default function PlanCardsGrid({ org_id, onSuccess, source = "plan_cards", upgradePromptId }) {
  const { data: org } = useOrganisation({ org_id });
  const dispatch = useDispatch();
  const { startCheckout, createUpgradeHostedPageFn, resumeSubscription } =
    useBilling({ orgId: org_id });
  const [loadingPlanId, setLoadingPlanId] = useState(null);

  const plans = getVisiblePlans();
  const currentFamily = getPlanFamily(org?.plan_id);
  const currentTierRank = getTierRank(currentFamily);
  const isFreePlanUser = currentTierRank <= getTierRank("free");

  const handleUpgrade = async (plan) => {
    if (loadingPlanId) return;
    const key = plan.plan_id_usd || plan.plan_id_inr;
    setLoadingPlanId(key);
    try {
      const planId =
        org.plan_id?.indexOf("usd") !== -1 ? plan.plan_id_usd : plan.plan_id_inr;

      dispatch(addMixPanelEvent({
        orgId: org_id, body: {
          eventName: "UPGRADE_PROMPT_CLICKED",
          eventData: {
            upgrade_prompt_id: upgradePromptId,
            plan_id: planId,
            plan_tier: getPlanTier(planId),
            upgrade_source: source,
          }
        }
      }));

      dispatch(addMixPanelEvent({
        orgId: org_id, body: {
          eventName: "SELECT_PLAN",
          eventData: {
            plan_id: planId,
            plan_tier: getPlanTier(planId),
            source,
          }
        }
      }));

      const url = window.location.href.split("?")[0];
      const hostedPageFn = createUpgradeHostedPageFn({
        planId,
        redirectURL: url,
        cancelURL: url,
        source,
      });
      await startCheckout({
        hostedPageFn,
        afterValidate: async () => { onSuccess?.(); },
        onClose: () => {},
        layout: "full_page",
        planId,
      });
    } catch (_) {
      // errors already toasted by useBilling
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleResume = async () => {
    try {
      await resumeSubscription();
      onSuccess?.();
    } catch (_) {}
  };

  return (
    <div className="plan-cards-grid">
      {plans.map((plan) => {
        const isCurrent = isCurrentPlan(org?.plan_id, plan);
        const isPopular = isPopularPlan(plan);
        const planKey = plan.plan_id_usd || plan.plan_id_inr || plan.plan_title;
        const isLoading = loadingPlanId === planKey;

        const cardTierRank = getTierRank(plan.plan_family);
        const isUpgrade = cardTierRank > currentTierRank;
        const ctaLabel = isUpgrade ? "Upgrade" : "Downgrade";

        const isFreeCard = isFreePlanId(plan.plan_id_usd);
        const showDiscount = isFreePlanUser && !!plan.original_price;
        const effectivePriceStr = isFreeCard
          ? "$0"
          : (!isFreePlanUser && plan.original_price ? plan.original_price : plan.price)?.toString() ?? "";
        const hasDollar = effectivePriceStr.startsWith("$");
        const priceNum = hasDollar ? effectivePriceStr.slice(1) : effectivePriceStr;

        return (
          <div
            key={planKey}
            style={{
              background: C.w,
              border: `${isCurrent ? 2 : 1}px solid ${isCurrent ? C.p400 : C.n200}`,
              borderRadius: 13,
              padding: 18,
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {/* Most Popular badge */}
            {isPopular && (
              <div style={{
                position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                background: C.p600, color: C.w, borderRadius: 999,
                fontSize: 9.5, fontWeight: 700, padding: "3px 10px",
                textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap",
              }}>
                Most Popular
              </div>
            )}

            {/* Plan name + description */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.n900, marginBottom: 4 }}>
                {plan.plan_title}
              </div>
              <div style={{ fontSize: 11, color: C.n500, lineHeight: 1.45, marginBottom: 8, minHeight: 42 }}>
                {plan.plan_sub_title}
              </div>

              {/* Price */}
              {hasDollar ? (
                <>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: 2 }}>
                    {showDiscount && (
                      <span style={{ fontSize: 13, color: C.n400, textDecoration: "line-through", marginBottom: 3 }}>
                        {plan.original_price}
                      </span>
                    )}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <span style={{ fontSize: 12, color: C.n700, fontWeight: 600, marginTop: 2 }}>$</span>
                      <span style={{ fontSize: 28, fontWeight: 800, color: isCurrent ? C.p600 : C.n900, lineHeight: 1 }}>
                        {priceNum}
                      </span>
                    </div>
                  </div>
                  <div style={{ minHeight: 32 }}>
                    {!showDiscount && plan.price_subtitle && (
                      <div style={{ fontSize: 11.5, color: C.n400 }}>{plan.price_subtitle}</div>
                    )}
                    {showDiscount && plan.discount_note && (
                      <div style={{ fontSize: 11, color: C.n400, marginTop: 3, fontStyle: "italic" }}>
                        {plan.discount_note}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.n900, lineHeight: 1, marginBottom: 2 }}>
                    {effectivePriceStr}
                  </div>
                  <div style={{ fontSize: 11.5, color: C.n400 }}>{plan.price_subtitle}</div>
                </>
              )}
            </div>

            {/* CTA button */}
            <div style={{ marginBottom: 12 }}>
              {plan.cta ? (
                <a href={plan.cta.link} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                  <button style={{
                    width: "100%", padding: "8px 0", border: `1.5px solid ${C.p400}`,
                    borderRadius: 7, background: "transparent", color: C.p600,
                    fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  }}>
                    {plan.cta.text}
                  </button>
                </a>
              ) : isLoading ? (
                <button disabled style={{
                  width: "100%", padding: "8px 0", border: "none", borderRadius: 7,
                  background: isPopular ? C.n900 : C.p600, color: C.w, fontSize: 12.5, fontWeight: 600,
                  cursor: "default", display: "flex", justifyContent: "center", alignItems: "center", gap: 6,
                }}>
                  <Spinner color="white" />
                </button>
              ) : isCurrent ? (
                <button
                  onClick={org?.subscription_status === "non_renewing" ? handleResume : undefined}
                  disabled={org?.subscription_status !== "non_renewing"}
                  style={{
                    width: "100%", padding: "8px 0", border: `1.5px solid ${C.p400}`,
                    borderRadius: 7, background: "transparent", color: C.p600,
                    fontSize: 12.5, fontWeight: 600,
                    cursor: org?.subscription_status === "non_renewing" ? "pointer" : "default",
                  }}
                >
                  {org?.subscription_status === "non_renewing" ? "Resume Plan" : "Current Plan ✓"}
                </button>
              ) : isFreeCard ? (
                <button
                  disabled
                  title="Cancel your subscription to move back to the Free plan"
                  style={{
                    width: "100%", padding: "8px 0", border: `1.5px solid ${C.n200}`,
                    borderRadius: 7, background: "transparent", color: C.n400,
                    fontSize: 12.5, fontWeight: 600, cursor: "default",
                  }}
                >
                  Downgrade
                </button>
              ) : (
                <button
                  onClick={() => handleUpgrade(plan)}
                  disabled={!!loadingPlanId}
                  style={{
                    width: "100%", padding: "8px 0",
                    border: `1.5px solid ${isUpgrade && isPopular ? C.n900 : isUpgrade ? C.p400 : C.n300}`,
                    borderRadius: 7,
                    background: isUpgrade && isPopular ? C.n900 : "transparent",
                    color: isUpgrade && isPopular ? C.w : isUpgrade ? C.p600 : C.n500,
                    fontSize: 12.5, fontWeight: 600,
                    cursor: loadingPlanId ? "default" : "pointer",
                    opacity: loadingPlanId ? 0.6 : 1,
                  }}
                >
                  {ctaLabel}
                </button>
              )}
            </div>

            <div style={{ height: 1, background: C.n100, marginBottom: 10 }} />

            {/* Feature list */}
            {plan.subtitle1 && (
              <div style={{ fontSize: 11, fontWeight: 600, color: C.n500, marginBottom: 8 }}>
                {plan.subtitle1}
              </div>
            )}
            <div style={{ flex: 1 }}>
              {plan.summary_features?.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 7 }}>
                  <CheckIcon />
                  <span style={{ fontSize: 11.5, color: C.n700, lineHeight: 1.45 }}>
                    <Info text={f.text} helpText={f.help_text} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
